import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import { Grid, Box, Tabs, Tab } from '@mui/material';
import {
  subscriptionQueries
} from '../../../graphql/queries';
import AdminPage from '../../../Components/AdminPage.js';
import AdminBackButton from '../../../Components/AdminBackButton.js';
import SubscriptionForm from './SubscriptionForm.js';
import Articles from './Articles.js';
import SubscriptionFormWrapper from './SubscriptionFormWrapper.js';

const subscriberColumns = [
  { id: 'name', label: 'Name', sortColumn: 'lastName' },
  { id: 'email', label: 'Email', sortColumn: 'email' },
  { id: 'planType', label: 'Plan', sortColumn: 'planType' },
  { id: 'startDateTime', label: 'Start date', sortColumn: 'startDateTime' },
  { id: 'endDateTime', label: 'End date', sortColumn: 'endDateTime' },
  { id: 'status', label: 'Status', sortColumn: 'status' }
];

// Server-side status filter. 'All' maps to no filter in the resolver; the others match
// the three derived states. The dropdown uses `name` as both option value and label
// (AdminTableFilter forces valueField=keyField='name').
const statusFilterItems = [
  { name: 'All' },
  { name: 'Active' },
  { name: 'Cancelling' },
  { name: 'Expired' }
];

// AdminPage/BBTable key rows by `id` and render each cell via _.get(row, column.id) as
// plain text, so any display formatting happens here. The resolver returns userId +
// separate first/last names, so map a stable `id` for the row key and a combined `name`;
// capitalise the derived status; format the start date as 'MMMM Do YYYY' (e.g. June 12th
// 2026) to match the admin convention (cf. the sibling Articles tab, Articles.js). Sorting
// is unaffected — it runs server-side on the real start_date_time column. Guard against the
// loader's transient null rows.
const addSubscriberFields = rows => (rows || []).map(row => ({
  ...row,
  id: row.userId,
  name: [row.firstName, row.lastName].filter(Boolean).join(' '),
  status: row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : row.status,
  startDateTime: row.startDateTime ? moment(row.startDateTime).format('MMMM Do YYYY') : row.startDateTime,
  // null end date (active) falls through unformatted → BBTable renders '-'
  endDateTime: row.endDateTime ? moment(row.endDateTime).format('MMMM Do YYYY') : null
}));

const EditSubscription = () => {
  const { subscriptionId } = useParams();
  const [tab, setTab] = useState(0);
  const [statusFilter, setStatusFilter] = useState('All');
  const { data } = useQuery(subscriptionQueries.SubscriptionByIdAdmin(subscriptionId), {
    fetchPolicy: 'no-cache'
  });

  if (!data) {
    return null;
  }

  const subscription = { ...data.subscriptionByIdAdmin };

  // Getting yearly/monthly price id
  const getPriceIdForPlan = (plans, type) => {
    const plan = plans.find(plan => plan.type === type);
    return plan.price.id;
  };

  subscription.yearlyPriceId = getPriceIdForPlan(subscription.subscriptionPlans, 'Yearly');
  subscription.monthlyPriceId = getPriceIdForPlan(subscription.subscriptionPlans, 'Monthly');

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <AdminBackButton destination='/admin/subscriptions' />
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(event, newValue) => setTab(newValue)}>
          <Tab label='Overview' />
          <Tab label='Subscribers' />
        </Tabs>
      </Box>
      {
        tab === 0
          ? (
            <Grid
              container
              spacing={2}
            >
              <Grid xs={6} item>
                <SubscriptionFormWrapper>
                  {({ authors, prices, tags, publishers }) => (
                    <SubscriptionForm
                      subscription={subscription}
                      authors={authors}
                      prices={prices}
                      tags={tags}
                      publishers={publishers}
                      showBackButton={false}
                    />
                  )}
                </SubscriptionFormWrapper>
              </Grid>
              <Grid xs={6} item>
                <Articles
                  subscription={subscription}
                />
              </Grid>
            </Grid>
            )
          : (
            <AdminPage
              columns={subscriberColumns}
              query={subscriptionQueries.SubscriptionSubscribersAdmin}
              fetchPolicy='no-cache'
              queryOptions={{ subscriptionId: Number(subscriptionId), status: statusFilter }}
              initialOrderBy='startDateTime'
              initialOrderByDirection='desc'
              rowsDataPath='subscriptionSubscribersAdmin.subscribers'
              countDataPath='subscriptionSubscribersAdmin.count'
              transformRows={addSubscriberFields}
              additionalDropdown={{
                id: 'subscriberStatusSelect',
                name: 'subscriberStatus',
                label: 'Status',
                items: statusFilterItems,
                value: statusFilter,
                onChange: ({ value }) => setStatusFilter(value)
              }}
              sectionName={`subscription-subscribers-${subscriptionId}`}
              onRowClick={() => {}}
            />
            )
      }
    </>
  );
};

export default EditSubscription;
